import React, { useState, useRef, useEffect } from "react";
import moment from "moment";

/** Utilities */
import formatDate from "../../lib/formatDate";
import { TimeLogActivities } from "../../utils/Enums/timelogActivityEnums";

/** Common Components */
import Label from "../../components/ui/label/Label";
import Tooltip from "../../components/ui/tooltip/Tooltip";
import NoRecordFound from "../ui/noRecordFound/NoRecordFound";
import Iconify from "../../layouts/components/iconify/iconify";
import DataLoader from "../FinalMolGrid/ui/dataLoader/DataLoader";
import DatePickerComponent from "../finalForms/ui/inputs/datePicker/DatePicker";

/** Common Services & Data Files */
import SwalAlert from "../../services/swalService/SwalService";
import ToastService from "../../services/toastService/ToastService";

/** CSS Files */
import "./Accordion.scss";

import { useAddTimeSheetRecordMutation, } from "../../app/services/timeSheetLogAPI";

const Accordion = ({
  data,
  onEdit,
  onDelete,
  onTaskCodeClick,
  isLoading,
  startDate,
  isActionShow,
  handleSuccess,
  descriptionTooltipOnlyOnOverflow = false,
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [description, setDescription] = useState("");
  const [taskDetails, setTaskDetails] = useState(null);
  const [editingTask, setEditingTask] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const textRefs = useRef({});
  const [overflowingDescriptions, setOverflowingDescriptions] = useState({});

  useEffect(() => {
    if (!descriptionTooltipOnlyOnOverflow) return;

    const checkOverflow = () => {
      const next = {};
      Object.entries(textRefs.current).forEach(([id, element]) => {
        if (element) {
          next[id] = element.scrollHeight > element.clientHeight ||
            element.scrollWidth > element.clientWidth;
        }
      });
      setOverflowingDescriptions(next);
    };

    const observer = new ResizeObserver(checkOverflow);
    Object.values(textRefs.current).forEach((element) => {
      if (element) observer.observe(element);
    });
    checkOverflow();
    return () => observer.disconnect();
  }, [descriptionTooltipOnlyOnOverflow, data, expandedSections, editingTask, taskDetails]);

  const { error } = SwalAlert();
  const [
    addTimeSheetRecord,
    {
      isSuccess: isTimeSheetRecordAdded,
      data: addTimeSheetRecordData,
    },
  ] = useAddTimeSheetRecordMutation();

  // const [
  //   getAllTimeSheetRecords,
  //   {
  //     isLoading: isAllRecordsLoading,
  //     isFetching: isAllRecordsFetching,
  //     isSuccess: isAllRecordsSuccess,
  //     data: timeSheetRecordsData,
  //   },
  // ] = useLazyGetAllTimeSheetRecordsQuery();

  useEffect(() => {

    if (isTimeSheetRecordAdded && addTimeSheetRecordData) {
      if (addTimeSheetRecordData?.errorMessage?.includes("exists")) {
        ToastService.error(addTimeSheetRecordData.errorMessage);
        return;
      } else {
        ToastService.success(addTimeSheetRecordData.errorMessage);
        // Fetch timesheet list for the selected week
        setTaskDetails(null);
        handleSuccess();
        // let request = {
        //   userId: UserId,
        //   startDate: startDate,
        // };
        // getAllTimeSheetRecords(request);
      }
    }
  }, [isTimeSheetRecordAdded, addTimeSheetRecordData]);

  const toggleSection = (day) => {
    setExpandedSections((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
    setEditingTask("");
  };

  const getHeaderClass = (totalHours) => {
    if (totalHours === 0) return "white";
    return totalHours >= 8.5 ? "green" : "red";
  };

  const handleEditClick = (data) => {
    onEdit?.(data);
  };

  const handleDeleteClick = (data) => {
    onDelete?.(data);
  };


  const handleEditTask = (task, field) => {

    setTaskDetails(task);
    setEditingTask(field);
    setDescription(task?.workDescription);
    setStartTime(moment(task?.startTime, "HH:mm:ss").toDate());
    setEndTime(moment(task?.endTime, "HH:mm:ss").toDate());
  };

  const filterPassedTime = (time) => {

    if (!startTime) return true;
    const currentDate = new Date(startTime);
    const selectedDate = new Date(time);

    return currentDate.getTime() <= selectedDate.getTime();
  };

  const handleUpdateTask = (task) => {
    if (!startTime || !endTime) {
      error("Please fill all the required fields.", "Validation Failed!");
      return;
    }
    // const startTimeData = formatDate(startTime, "HH:mm:ss");
    // const endTimeData = formatDate(endTime, "HH:mm:ss");

    const startTimeData = moment(startTime).format("HH:mm:ss");
    const endTimeData = moment(endTime).format("HH:mm:ss");

    if (startTimeData >= endTimeData) {

      error("Start time must be earlier than end time.", "Invalid Time Range!");
      setEndTime(null)
      return;
    }

    let req = {
      timesheetDetailId: task?.timesheetDetailId,
      timesheetId: task?.timesheetId,
      userId: task?.userId,
      projectId: task?.projectId,
      taskId: task?.taskId,
      taskStatusId: task?.statusId,
      timeLogActivityId: task?.timeLogActivityId,
      timeLogSubActivityId: task?.timeLogSubActivityId,
      startTime: startTimeData,
      endTime: endTimeData,
      WorkDescription: description ? description : task?.workDescription,
      workAt: task?.workAt,
    };
    addTimeSheetRecord(req);
    setEditingTask("");
    setTaskDetails(null);
  };


  const formatWorkingHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  return (
    <div>
      {isLoading && <DataLoader />}
      {data?.length > 0 ? (
        <div className="accordion">
          {data.map((dayData, index) => {
            const totalHours = Number(dayData.totalWorkingHours);
            const isLeaveDay = dayData.taskList?.some(
              (task) => task.timeLogActivityId === TimeLogActivities.LEAVE
            );
            return (
              <div key={index + 1} className="accordion-section">
                <button
                  style={{
                    border: "unset",
                    background: "unset",
                    width: "100%",
                    padding: 0,
                  }}
                  onClick={() => toggleSection(dayData.startDate)}
                >
                  <div
                    className={`accordion-header ${getHeaderClass(totalHours)}`}
                  >
                    <span className="accordion-title">
                      <Iconify icon="uil:calender" width={20} />
                      {formatDate(dayData?.startDate, "DD MMM, YYYY (dddd)")}
                      {isLeaveDay && (
                        <span
                          title="This day contains a Leave entry."
                          style={{ cursor: "alias", display: "inline-flex", marginLeft: "6px" }}
                        >
                          <Iconify
                            icon="ic:outline-info"
                            color="#3396ff"
                            width={20}
                            title="This day contains a Leave entry."
                          />
                        </span>
                      )}
                    </span>
                    <span className="day-total">
                      <Iconify icon="solar:stopwatch-broken" width={24} />{" "}
                      {formatWorkingHours(totalHours)}
                      {/* {totalHours.toFixed(2)} hrs */}
                    </span>
                  </div>
                </button>
                {expandedSections[dayData.startDate] && (
                  <div className="accordion-content">
                    <table>
                      <thead>
                        <tr>
                          {/* <th>Project ID</th> */}
                          <th>Activity</th>
                          <th>Sub Activity</th>
                          <th>Work Description</th>
                          <th>Task</th>
                          <th>Project Name</th>
                          <th>Time</th>
                          <th>Hours</th>
                          {isActionShow && <th>Action</th>}
                        </tr>
                      </thead>

                      <tbody>
                        {dayData?.taskList.map((task, i) => (
                          <tr
                            key={i + 1}
                            className="task "
                            onMouseEnter={() =>
                              setHoveredTaskId(task?.timesheetDetailId)
                            }
                            onMouseLeave={() => setHoveredTaskId(null)}
                          >
                            <td data-label="Activity" className="task-activity">
                              {task.activityName}
                            </td>
                            <td data-label="Sub Activity" className="task-subActivity">
                              {task.subActivityName}
                            </td>
                            <td data-label="Work Description" className="task-details">
                              {taskDetails?.timesheetDetailId ===
                                task?.timesheetDetailId &&
                                editingTask === "description" ? (
                                <div className="edit-description">
                                  <input
                                    value={description}
                                    onChange={(e) =>
                                      setDescription(e.target.value)
                                    }
                                    className="border rounded p-1"
                                  />
                                  <div className="action-btn">
                                    <button
                                      className="save-btn"
                                      onClick={() => handleUpdateTask(task)}
                                    >
                                      <Iconify icon="mdi:tick" />
                                    </button>
                                    <button
                                      className="cancel-btn"
                                      onClick={() => setTaskDetails({})}
                                    >
                                      <Iconify icon="iconoir:cancel" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="work-description-wrapper">
                                  <span
                                    className="work-description-text"
                                    ref={(el) => (textRefs.current[task.timesheetDetailId] = el)}
                                  >
                                    {task.workDescription}
                                  </span>

                                  <div className="work-description-actions">
                                    <span
                                      id={`preview-${task.timesheetDetailId}`}
                                      className="work-description-preview"
                                      style={descriptionTooltipOnlyOnOverflow && !overflowingDescriptions[task.timesheetDetailId]
                                        ? { visibility: "hidden" } : undefined}
                                    >
                                      <Iconify icon="mdi:information-outline" />
                                    </span>

                                    {(!descriptionTooltipOnlyOnOverflow || overflowingDescriptions[task.timesheetDetailId]) && <Tooltip
                                      targetId={`preview-${task.timesheetDetailId}`}
                                      label={task.workDescription}
                                      position="bottom"
                                      variant="dark"
                                      className="work-description-tooltip"
                                    />}

                                    {isActionShow && (
                                      <span
                                        className="work-description-edit"
                                        onClick={() => handleEditTask(task, "description")}
                                      >
                                        <Iconify icon="line-md:pencil" />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td data-label="Task" className="task-title">
                              {task.title ? task.title : "-"}
                            </td>
                            <td data-label="Project Name" className="task-tag">
                              {task.projectName ? task.projectName : "-"}
                            </td>
                            <td data-label="Time" className="task-timing">
                              <div className="task-time-picker">
                                {taskDetails?.timesheetDetailId ===
                                  task?.timesheetDetailId &&
                                  editingTask === "time" ? (
                                  <>
                                    <div className="edit-time-range edit-icon-buttons">
                                      {/* Start Time Picker */}
                                      <div className="time-range">
                                        <div className="time-box validation-left-0 mt-1 no-icon">
                                          <Label
                                            labelName="&nbsp;"
                                            isRequired={false}
                                          />
                                          <DatePickerComponent
                                            isDisable={false}
                                            selected={startTime}
                                            onChange={(date) => {
                                              if (endTime && date >= endTime) {
                                                setEndTime(null);
                                              }
                                              setStartTime(date);
                                            }

                                            }
                                            placeholder={"Start Time"}
                                            datePickerConfig={{
                                              timeInputLabel: "Time:",
                                              displayTimeSelect: true,

                                              timeIntervals: 15,
                                              displayTimeSelectOnly: true,
                                              format: "h:mm aa",
                                            }}
                                          />
                                        </div>
                                        <div className="time-range-badge">
                                          <Label
                                            labelName="-"
                                            isRequired={false}
                                          />
                                        </div>
                                        <div className="time-box validation-left-0 mt-1  no-icon">
                                          <Label
                                            labelName="&nbsp;"
                                            isRequired={false}
                                          />
                                          <DatePickerComponent
                                            isDisable={false}
                                            selected={endTime}
                                            onChange={(date) =>

                                              setEndTime(date)

                                            }
                                            placeholder={"End Time"}
                                            datePickerConfig={{
                                              filteredTime: filterPassedTime,
                                              timeInputLabel: "Time:",
                                              displayTimeSelect: true,

                                              timeIntervals: 15,
                                              displayTimeSelectOnly: true,
                                              format: "h:mm aa",
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <button
                                        className="save-btn"
                                        onClick={() => handleUpdateTask(task)}
                                      >
                                        <Iconify icon="mdi:tick" />
                                      </button>
                                      <button
                                        className="cancel-btn"
                                        onClick={() => setTaskDetails({})}
                                      >
                                        <Iconify icon="iconoir:cancel" />
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <span className="task-time">
                                      {moment(
                                        task.startTime,
                                        "HH:mm:ss"
                                      ).format("hh:mm A")}{" "}
                                      -{" "}
                                      {moment(task.endTime, "HH:mm:ss").format(
                                        "hh:mm A"
                                      )}
                                    </span>
                                    &nbsp;
                                    {isActionShow &&
                                      task?.timesheetDetailId ===
                                      hoveredTaskId && (
                                        <span>
                                          <Iconify
                                            icon="line-md:pencil"
                                            onClick={() =>
                                              handleEditTask(task, "time")
                                            }
                                          />
                                        </span>
                                      )}
                                  </>
                                )}
                              </div>
                            </td>
                            <td data-label="Hours" className="task-total-time">
                              <span>
                                {formatWorkingHours(task.workingHours)}
                              </span>
                            </td>

                            {isActionShow && (
                              <td data-label="Action" className="task-actions">
                                <span>
                                  <button
                                    onClick={() => handleEditClick(task)}
                                    className="edit"
                                    title="Edit"
                                  >
                                    <Iconify icon="line-md:pencil" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(task)}
                                    className="more-options"
                                    title="More Options"
                                  >
                                    <Iconify icon="mingcute:delete-2-line" />
                                  </button>
                                </span>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <NoRecordFound />
      )}
    </div>
  );
};

export default Accordion;
